/**
 * Trading Hub — single-file portal.
 * Rotte:
 *   /th-portal            -> login / dashboard (in base al token)
 *   /th-portal/settings   -> utenti + target
 *   /th-portal/e/:id      -> dettaglio evento
 *
 * Nessun link in ingresso: si accede solo con l'URL e le credenziali.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Routes, Route, useNavigate, useParams, Link, Navigate } from 'react-router-dom';
import { LineChart as RCLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ComposedChart, Bar, Cell } from 'recharts';
import api, { getToken, setToken, clearToken } from './api';
import {
  LogIn, LogOut, LineChart, Radio, Send, Trash2, Copy, RefreshCw, Filter,
  Users as UsersIcon, Target as TargetIcon, X, Shield, Check, AlertTriangle, ChevronRight,
  Download, FileSpreadsheet, TrendingUp, Cloud, Play, ExternalLink, Pause, Zap
} from 'lucide-react';

// ── Shared UI atoms ─────────────────────────────────────────────────
const shellBg = 'min-h-screen bg-slate-950 text-slate-100';
const cardCls = 'bg-slate-900/70 border border-slate-800 rounded-lg';
const inputCls = 'w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-md text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30';
const btnPrim = 'inline-flex items-center gap-2 px-4 py-2 bg-primary text-slate-950 font-bold rounded-md hover:bg-primary-hover transition disabled:opacity-50';
const btnGhost = 'inline-flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-100 rounded-md text-sm';
const btnDanger = 'inline-flex items-center gap-2 px-3 py-1.5 bg-red-900/40 hover:bg-red-900/60 text-red-200 border border-red-900 rounded-md text-sm';

function useMe() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    if (!getToken()) { setMe(null); setLoading(false); return; }
    try { const r = await api.get('/api/trading-hub/auth/me'); setMe(r.data); }
    catch { setMe(null); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return { me, loading, reload: load, setMe };
}

// ── Login ───────────────────────────────────────────────────────────
function Login({ onLogged }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const r = await api.post('/api/trading-hub/auth/login', { email, password });
      setToken(r.data.token);
      onLogged(r.data.user);
    } catch (ex) {
      setErr(ex.response?.data?.detail || 'Credenziali non valide');
    } finally { setBusy(false); }
  };
  return (
    <div className={`${shellBg} flex items-center justify-center p-4`}>
      <form onSubmit={submit} className={`${cardCls} w-full max-w-sm p-6`} data-testid="th-login-form">
        <div className="flex items-center gap-2 mb-1">
          <Shield size={18} className="text-primary" />
          <span className="text-xs font-bold tracking-widest text-slate-400 uppercase">Trading Hub</span>
        </div>
        <h1 className="text-2xl font-black mb-4">Accesso riservato</h1>
        {err && (
          <div className="mb-3 p-2 bg-red-900/30 border border-red-900 text-red-200 text-xs rounded flex items-center gap-1" data-testid="th-login-error">
            <AlertTriangle size={12} /> {err}
          </div>
        )}
        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Email</label>
        <input data-testid="th-email" type="email" autoFocus className={`${inputCls} mb-3`} value={email} onChange={(e) => setEmail(e.target.value)} />
        <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1">Password</label>
        <input data-testid="th-password" type="password" className={`${inputCls} mb-4`} value={password} onChange={(e) => setPassword(e.target.value)} />
        <button data-testid="th-login-submit" className={`${btnPrim} w-full justify-center`} disabled={busy}>
          <LogIn size={16} /> {busy ? 'Accesso...' : 'Accedi'}
        </button>
        <p className="text-[10px] text-slate-500 mt-4 text-center">Area privata. Ogni accesso viene registrato.</p>
      </form>
    </div>
  );
}

// ── Header / top nav ────────────────────────────────────────────────
function TopBar({ me, onLogout, active }) {
  return (
    <header className="sticky top-0 z-30 bg-slate-950/95 backdrop-blur border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Shield size={18} className="text-primary" />
            <span className="font-black text-sm tracking-tight">TRADING HUB</span>
          </div>
          <nav className="flex items-center gap-1">
            <Link to="/th-portal" className={`px-3 py-1.5 rounded-md text-sm font-semibold ${active === 'home' ? 'bg-primary text-slate-950' : 'text-slate-300 hover:bg-slate-800'}`} data-testid="th-nav-home">
              <LineChart size={14} className="inline -mt-0.5 mr-1" /> Eventi
            </Link>
            <Link to="/th-portal/analytics" className={`px-3 py-1.5 rounded-md text-sm font-semibold ${active === 'analytics' ? 'bg-primary text-slate-950' : 'text-slate-300 hover:bg-slate-800'}`} data-testid="th-nav-analytics">
              <TrendingUp size={14} className="inline -mt-0.5 mr-1" /> Analytics
            </Link>
            {me?.is_owner && (
              <Link to="/th-portal/settings" className={`px-3 py-1.5 rounded-md text-sm font-semibold ${active === 'settings' ? 'bg-primary text-slate-950' : 'text-slate-300 hover:bg-slate-800'}`} data-testid="th-nav-settings">
                <TargetIcon size={14} className="inline -mt-0.5 mr-1" /> Impostazioni
              </Link>
            )}
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400 hidden sm:inline">{me?.email}</span>
          <button onClick={onLogout} className={btnGhost} data-testid="th-logout">
            <LogOut size={13} /> Esci
          </button>
        </div>
      </div>
    </header>
  );
}

// ── Dashboard / Events list ─────────────────────────────────────────
function HealthBar() {
  const [h, setH] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api.get('/api/trading-hub/health'); setH(r.data); } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);
  if (!h) return null;
  const secs = h.seconds_since_last_webhook;
  const market = h.market_status || {};
  // Colore basato su seconds_since + market.is_open
  let color = 'emerald', label = 'OK';
  if (secs == null) { color = 'slate'; label = 'Nessun webhook'; }
  else if (secs < 300) { color = 'emerald'; label = `${secs}s fa`; }
  else if (secs < 1800) { color = 'yellow'; label = `${Math.round(secs/60)} min fa`; }
  else if (market.is_open) { color = 'red'; label = `⚠ ${Math.round(secs/60)} min fa (mercato aperto!)`; }
  else { color = 'slate'; label = `${Math.round(secs/60)} min fa`; }
  const colorMap = {
    emerald: 'bg-emerald-950/40 border-emerald-800 text-emerald-200',
    yellow: 'bg-amber-950/40 border-amber-800 text-amber-200',
    red: 'bg-red-950/40 border-red-800 text-red-200',
    slate: 'bg-slate-900/40 border-slate-800 text-slate-300',
  };
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3`} data-testid="th-health-bar">
      <div className={`${cardCls} p-3 border-l-4 border-l-${color}-500 ${colorMap[color]}`}>
        <p className="text-[10px] font-bold uppercase tracking-widest opacity-70">Ultimo webhook</p>
        <p className="text-lg font-black mt-0.5">{label}</p>
        <p className="text-[11px] opacity-70 mt-0.5">
          {h.last_webhook_symbol ? <>Simbolo: <span className="font-mono">{h.last_webhook_symbol}</span></> : 'Nessun simbolo estratto'}
          {h.last_webhook_at ? <> · {new Date(h.last_webhook_at).toLocaleString('it-IT')}</> : ''}
        </p>
      </div>
      <div className={`${cardCls} p-3 border-l-4 ${market.is_open ? 'border-l-emerald-500 bg-emerald-950/30 text-emerald-100' : 'border-l-slate-600 bg-slate-900/50 text-slate-200'}`}>
        <p className="text-[10px] font-bold uppercase tracking-widest opacity-70">Mercato {market.exchange || 'NYSE'} <span className="text-slate-500 font-normal ml-1">({market.exchange_iso || '—'})</span></p>
        <p className="text-lg font-black mt-0.5">{market.is_open ? 'APERTO' : 'CHIUSO'} <span className="text-xs font-normal opacity-70">· {market.now_local_time || market.now_ny_time}</span></p>
        <p className="text-[11px] opacity-70 mt-0.5">{market.reason} · {market.next_change_kind === 'close' ? 'chiude' : 'apre'} {market.next_change ? new Date(market.next_change).toLocaleString('it-IT') : ''}</p>
        {market.source === 'fallback_hardcoded' && (
          <p className="text-[9px] mt-0.5 text-amber-400/80" title="La libreria exchange_calendars non ha una calendar per questo exchange — usato fallback statico NYSE.">⚠ fallback statico</p>
        )}
      </div>
    </div>
  );
}

function Dashboard({ me }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, last_24h: 0, by_symbol: [] });
  const [filters, setFilters] = useState({ symbol: '', event_type: '', status: '', q: '' });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'by_symbol' | 'by_sync_status'
  const [groups, setGroups] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const [ev, st] = await Promise.all([
        api.get('/api/trading-hub/events', { params: { ...params, limit: 100 } }),
        api.get('/api/trading-hub/stats'),
      ]);
      setItems(ev.data.items || []);
      setTotal(ev.data.total || 0);
      setStats(st.data);
      setChecked(new Set()); // reset selection quando ricarico
    } catch { /* silently */ }
    finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  // Carica raggruppamenti quando cambia viewMode
  useEffect(() => {
    if (viewMode === 'list') return;
    const by = viewMode === 'by_symbol' ? 'symbol' : 'sync_status';
    api.get(`/api/trading-hub/event-groups?by=${by}`).then(r => setGroups(r.data || [])).catch(() => setGroups([]));
  }, [viewMode]);

  // Auto-refresh ogni 15s (piu' reattivo per un webhook receiver)
  useEffect(() => {
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const fmt = (d) => d ? new Date(d).toLocaleString('it-IT') : '—';
  const toggleCheck = (id) => {
    const n = new Set(checked);
    if (n.has(id)) n.delete(id); else n.add(id);
    setChecked(n);
  };
  const toggleCheckAll = () => {
    if (checked.size === items.length) setChecked(new Set());
    else setChecked(new Set(items.map(i => i.id)));
  };
  const bulkAction = async (action) => {
    if (checked.size === 0) return;
    const labels = { retry_sync: 'rimettere in coda sync', mark_ignored: 'marcare come ignorati', delete: 'ELIMINARE definitivamente', archive: 'archiviare' };
    if (!window.confirm(`Sicuro di voler ${labels[action]} ${checked.size} eventi selezionati?`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post('/api/trading-hub/events-bulk-action', { action, ids: Array.from(checked) });
      alert(`Fatti: ${r.data.affected} eventi${r.data.requeued != null ? ` · Rimessi in coda: ${r.data.requeued}` : ''}`);
      load();
    } catch (e) { alert('Errore: ' + (e.response?.data?.detail || e.message)); }
    finally { setBulkBusy(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <HealthBar />
      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className={`${cardCls} p-4`}>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Eventi totali</p>
          <p className="text-3xl font-black mt-1">{stats.total.toLocaleString('it-IT')}</p>
        </div>
        <div className={`${cardCls} p-4`}>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Ultime 24h</p>
          <p className="text-3xl font-black mt-1 text-primary">{stats.last_24h.toLocaleString('it-IT')}</p>
        </div>
        <div className={`${cardCls} p-4`}>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Top simboli</p>
          <div className="flex flex-wrap gap-1 mt-2">
            {stats.by_symbol.slice(0, 5).map(s => (
              <span key={s.symbol} className="text-[10px] font-mono bg-slate-800 px-2 py-0.5 rounded">
                {s.symbol} <span className="text-primary">{s.count}</span>
              </span>
            ))}
            {stats.by_symbol.length === 0 && <span className="text-xs text-slate-500">—</span>}
          </div>
        </div>
      </div>

      {/* Filters + View mode */}
      <div className={`${cardCls} p-3 mb-4 flex flex-wrap items-center gap-2`}>
        <Filter size={14} className="text-slate-500" />
        <input placeholder="Simbolo" className={`${inputCls} w-32`} value={filters.symbol} onChange={e => setFilters({ ...filters, symbol: e.target.value })} data-testid="th-filter-symbol" />
        <input placeholder="Event type" className={`${inputCls} w-40`} value={filters.event_type} onChange={e => setFilters({ ...filters, event_type: e.target.value })} data-testid="th-filter-eventtype" />
        <select className={`${inputCls} w-32`} value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} data-testid="th-filter-status">
          <option value="">Ogni stato</option>
          <option value="received">Ricevuto</option>
          <option value="processed">Processato</option>
          <option value="archived">Archiviato</option>
        </select>
        <input placeholder="Cerca nel payload..." className={`${inputCls} flex-1 min-w-[200px]`} value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })} data-testid="th-filter-q" />
        <div className="flex items-center gap-1 border border-slate-800 rounded-md p-0.5" data-testid="th-view-mode">
          {[{k:'list',l:'Elenco'},{k:'by_symbol',l:'Per simbolo'},{k:'by_sync_status',l:'Per stato sync'}].map(v => (
            <button key={v.k} onClick={() => setViewMode(v.k)} className={`text-xs px-2 py-1 rounded ${viewMode===v.k ? 'bg-primary text-slate-900 font-bold' : 'text-slate-400 hover:text-slate-200'}`} data-testid={`th-view-${v.k}`}>{v.l}</button>
          ))}
        </div>
        <button onClick={load} className={btnGhost} data-testid="th-refresh"><RefreshCw size={14} /> Aggiorna</button>
      </div>

      {/* Bulk action bar */}
      {checked.size > 0 && (
        <div className="sticky top-2 z-10 mb-3 bg-primary/15 border border-primary/40 rounded-md p-2 flex flex-wrap items-center gap-2 backdrop-blur" data-testid="th-bulk-bar">
          <span className="text-xs font-bold text-primary">{checked.size} selezionati</span>
          <button onClick={() => bulkAction('retry_sync')} disabled={bulkBusy} className={btnGhost} data-testid="th-bulk-retry"><RefreshCw size={11}/> Retry sync</button>
          <button onClick={() => bulkAction('mark_ignored')} disabled={bulkBusy} className={btnGhost} data-testid="th-bulk-ignore">Marca come ignorati</button>
          <button onClick={() => bulkAction('archive')} disabled={bulkBusy} className={btnGhost} data-testid="th-bulk-archive">Archivia</button>
          <button onClick={() => bulkAction('delete')} disabled={bulkBusy} className={btnDanger} data-testid="th-bulk-delete"><Trash2 size={11}/> Elimina</button>
          <button onClick={() => setChecked(new Set())} className="ml-auto text-[10px] text-slate-400 hover:text-white">Annulla selezione</button>
        </div>
      )}

      {/* Events list / grouped view */}
      <div className={`${cardCls} overflow-hidden`}>
        <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
          <p className="text-sm font-bold flex items-center gap-2"><Radio size={14} className="text-primary" /> Eventi ricevuti <span className="text-slate-500 font-normal">({total})</span></p>
          <div className="flex items-center gap-2">
            {me?.is_owner && <ReclassifyButton onDone={load} />}
            <span className="text-[10px] text-slate-500">Auto-refresh 15s</span>
          </div>
        </div>
        {viewMode !== 'list' ? (
          <div className="divide-y divide-slate-800">
            {groups.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">Nessun gruppo.</div>}
            {groups.map(g => {
              const key = String(g.key);
              const isOpen = expandedGroups.has(key);
              return (
                <div key={key}>
                  <button onClick={() => {
                    const n = new Set(expandedGroups);
                    if (n.has(key)) n.delete(key); else n.add(key);
                    setExpandedGroups(n);
                    // Se clicco un gruppo per simbolo, filtro anche la lista
                    if (viewMode === 'by_symbol') setFilters({ ...filters, symbol: g.symbol_raw || g.key });
                  }} className="w-full px-4 py-3 hover:bg-slate-800/50 flex items-center gap-3 text-left" data-testid={`th-group-${key}`}>
                    <ChevronRight size={14} className={`text-slate-500 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-bold">{g.symbol_display || g.key}</span>
                        <span className="text-[10px] text-slate-500">{g.count} eventi</span>
                      </div>
                      {(g.first_at || g.last_at) && (
                        <p className="text-[10px] text-slate-500 mt-0.5">
                          {g.first_at ? fmt(g.first_at) : '—'} → {g.last_at ? fmt(g.last_at) : '—'}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[10px]">
                      {g.synced > 0 && <span className="text-emerald-400">✓ {g.synced}</span>}
                      {g.pending > 0 && <span className="text-amber-400">⏳ {g.pending}</span>}
                      {g.errors > 0 && <span className="text-red-400">✗ {g.errors}</span>}
                    </div>
                  </button>
                </div>
              );
            })}
            <p className="text-[10px] text-slate-500 px-4 py-2 italic">Clicca un gruppo per filtrare la lista &quot;Elenco&quot; per quel simbolo.</p>
          </div>
        ) : loading && items.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">Caricamento...</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">Nessun evento con questi filtri.</div>
        ) : (
          <>
            {/* header with select-all */}
            <div className="px-4 py-2 border-b border-slate-800 bg-slate-900/60 flex items-center gap-3 text-[10px] font-bold uppercase tracking-widest text-slate-500">
              <input type="checkbox" onChange={toggleCheckAll} checked={checked.size > 0 && checked.size === items.length} data-testid="th-check-all" className="cursor-pointer"/>
              <span>seleziona tutti visibili ({items.length})</span>
            </div>
            <div className="divide-y divide-slate-800">
              {items.map(e => {
                const isChk = checked.has(e.id);
                return (
                  <div key={e.id} className={`px-4 py-3 flex items-center gap-3 transition ${isChk ? 'bg-primary/5' : 'hover:bg-slate-800/50'}`} data-testid={`th-event-row-${e.id}`}>
                    <input type="checkbox" checked={isChk} onChange={() => toggleCheck(e.id)} data-testid={`th-check-${e.id}`} className="cursor-pointer flex-shrink-0" onClick={(ev)=>ev.stopPropagation()}/>
                    <button onClick={() => setSelected(e)} className="flex-1 min-w-0 text-left flex items-center gap-3" data-testid={`th-event-${e.id}`}>
                      <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-sm font-bold">{e.symbol_display || e.symbol_raw || e.symbol || '—'}</span>
                          {e.event_type && <span className="text-[10px] bg-slate-800 px-1.5 py-0.5 rounded text-slate-300">{e.event_type}</span>}
                          {e.state && <span className="text-[10px] bg-primary/20 text-primary px-1.5 py-0.5 rounded">{e.state}</span>}
                          {e.timeframe && <span className="text-[10px] text-slate-500">{e.timeframe}</span>}
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5">{fmt(e.received_at)} · da {e.ip || '?'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {e.google_sync_status && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${e.google_sync_status === 'synced' ? 'bg-emerald-900/40 text-emerald-300' : e.google_sync_status === 'error' ? 'bg-red-900/40 text-red-300' : e.google_sync_status === 'ignored' ? 'bg-slate-800 text-slate-500' : 'bg-amber-900/40 text-amber-300'}`}>
                            {e.google_sync_status}
                          </span>
                        )}
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${e.status === 'processed' ? 'bg-emerald-900/40 text-emerald-300' : e.status === 'archived' ? 'bg-slate-800 text-slate-500' : 'bg-blue-900/40 text-blue-300'}`}>
                          {e.status}
                        </span>
                        <ChevronRight size={14} className="text-slate-600" />
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {selected && <EventDetailModal event={selected} onClose={() => setSelected(null)} onChanged={load} />}
    </div>
  );
}

function ReclassifyButton({ onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!window.confirm('Riclassifica gli eventi senza symbol_key rileggendo il payload JSON?')) return;
    setBusy(true);
    try {
      const r = await api.post('/api/trading-hub/reclassify-events', { only_missing: true });
      alert(`Riclassificati:\n- Analizzati: ${r.data.analyzed}\n- Classificati: ${r.data.classified}\n- Non classificati: ${r.data.unclassified}\n- JSON invalido: ${r.data.invalid_json}\n- Simboli trovati: ${r.data.distinct_symbols.join(', ') || '—'}`);
      onDone?.();
    } catch (e) { alert('Errore: ' + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={run} disabled={busy} className={btnGhost} data-testid="th-reclassify-btn">
      <RefreshCw size={12}/> {busy ? 'In corso…' : 'Riclassifica'}
    </button>
  );
}

// ── Event detail modal ──────────────────────────────────────────────
function EventDetailModal({ event, onClose, onChanged }) {
  const [full, setFull] = useState(event);
  const [targets, setTargets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(event.note || '');

  useEffect(() => {
    (async () => {
      const [e, t] = await Promise.all([
        api.get(`/api/trading-hub/events/${event.id}`),
        api.get('/api/trading-hub/targets'),
      ]);
      setFull(e.data);
      setTargets(t.data);
      setNote(e.data.note || '');
    })();
  }, [event.id]);

  const copyJson = () => {
    navigator.clipboard?.writeText(JSON.stringify(full.payload_json ?? full.payload_raw, null, 2));
  };
  const forward = async (target_id) => {
    setBusy(true);
    try {
      const r = await api.post(`/api/trading-hub/events/${event.id}/forward`, { target_id });
      const fwd = r.data.forward;
      alert(`Forward ${fwd.ok ? 'OK' : 'FALLITO'}\nStatus: ${fwd.status_code || fwd.error || 'n/d'}\n${(fwd.response_snippet || '').slice(0, 200)}`);
      const upd = await api.get(`/api/trading-hub/events/${event.id}`);
      setFull(upd.data);
      onChanged?.();
    } catch (ex) {
      alert('Errore: ' + (ex.response?.data?.detail || ex.message));
    } finally { setBusy(false); }
  };
  const setStatus = async (status) => {
    await api.put(`/api/trading-hub/events/${event.id}`, { status });
    const upd = await api.get(`/api/trading-hub/events/${event.id}`);
    setFull(upd.data);
    onChanged?.();
  };
  const saveNote = async () => {
    await api.put(`/api/trading-hub/events/${event.id}`, { note });
    onChanged?.();
  };
  const remove = async () => {
    if (!window.confirm('Eliminare definitivamente questo evento?')) return;
    await api.delete(`/api/trading-hub/events/${event.id}`);
    onChanged?.();
    onClose();
  };

  const sendToGoogle = async () => {
    const metaEid = full?.payload_json?.metadata?.event_id;
    if (!metaEid) {
      alert('Impossibile inviare: manca metadata.event_id nel payload (non è un webhook v0.3.2 valido).');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/api/trading-hub/sync/send/${encodeURIComponent(metaEid)}`);
      if (r.data?.ok) {
        alert(`Inviato a Google Apps Script.\nRisposta: ${JSON.stringify(r.data.response || r.data.message)}`);
      } else {
        alert(`Invio fallito: ${r.data?.message || 'errore sconosciuto'}`);
      }
    } catch (ex) {
      alert('Errore: ' + (ex.response?.data?.detail || ex.message));
    } finally { setBusy(false); }
  };

  const payloadText = useMemo(() => {
    try { return JSON.stringify(full.payload_json ?? JSON.parse(full.payload_raw), null, 2); }
    catch { return full.payload_raw || ''; }
  }, [full]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`${cardCls} w-full max-w-4xl max-h-[90vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Evento</p>
            <p className="font-mono text-lg font-bold">{full.symbol || '—'} <span className="text-slate-500 text-sm">· {full.event_type}</span></p>
            <p className="text-[11px] text-slate-500 mt-0.5">Ricevuto: {new Date(full.received_at).toLocaleString('it-IT')} · IP {full.ip || '?'}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-800 rounded" data-testid="th-event-close"><X size={18} /></button>
        </div>

        <div className="p-4 grid md:grid-cols-2 gap-4">
          {/* Payload */}
          <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Payload JSON</p>
              <button onClick={copyJson} className={btnGhost}><Copy size={12} /> Copia</button>
            </div>
            <pre className="text-[11px] font-mono bg-slate-950 border border-slate-800 rounded-md p-3 overflow-auto max-h-72 whitespace-pre-wrap break-all">{payloadText}</pre>
          </div>

          {/* Actions */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Stato</p>
            <div className="flex gap-2">
              <button onClick={() => setStatus('received')} className={btnGhost}><Radio size={12} /> Ricevuto</button>
              <button onClick={() => setStatus('processed')} className={btnGhost}><Check size={12} /> Processato</button>
              <button onClick={() => setStatus('archived')} className={btnGhost}>Archivia</button>
            </div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mt-4 mb-2">Nota</p>
            <textarea rows={2} className={inputCls} value={note} onChange={e => setNote(e.target.value)} placeholder="Nota interna..." data-testid="th-event-note" />
            <button onClick={saveNote} className={`${btnGhost} mt-1`}>Salva nota</button>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Forward a target</p>
            {targets.length === 0 ? (
              <p className="text-[11px] text-slate-500">Nessun target configurato. Aggiungine in <Link to="/th-portal/settings" className="text-primary underline">Impostazioni</Link>.</p>
            ) : (
              <div className="flex flex-col gap-1">
                {targets.map(t => (
                  <button key={t.id} disabled={busy} onClick={() => forward(t.id)} className={`${btnPrim} !py-1.5 !text-xs justify-between`} data-testid={`th-forward-${t.id}`}>
                    <span className="flex items-center gap-1.5"><Send size={12} /> {t.nome}</span>
                    <span className="font-mono text-[10px] opacity-70 truncate max-w-[180px]">{t.url}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Forward history */}
        {(full.forwards || []).length > 0 && (
          <div className="px-4 pb-3">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Storico forward</p>
            <div className="space-y-1">
              {full.forwards.map((f, i) => (
                <div key={i} className="text-[11px] bg-slate-950 border border-slate-800 rounded p-2 flex items-center justify-between">
                  <div>
                    <span className={`font-bold ${f.ok ? 'text-emerald-400' : 'text-red-400'}`}>{f.ok ? 'OK' : 'FAIL'}</span>
                    <span className="mx-1.5">·</span>{f.target_nome}
                    <span className="mx-1.5">·</span><span className="font-mono">{f.status_code || f.error || '?'}</span>
                  </div>
                  <span className="text-slate-500">{new Date(f.at).toLocaleString('it-IT')}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="p-4 border-t border-slate-800 flex justify-between items-center gap-2 flex-wrap">
          <button onClick={sendToGoogle} disabled={busy} className={btnPrim} data-testid="th-event-send-google">
            <Cloud size={13} /> Manda ora a Google Sheet
          </button>
          <button onClick={remove} className={btnDanger}><Trash2 size={12} /> Elimina evento</button>
        </div>
      </div>
    </div>
  );
}

// ── Settings: users + targets ───────────────────────────────────────
function Settings({ me }) {
  const [tab, setTab] = useState('users');
  if (!me?.is_owner) return <Navigate to="/th-portal" replace />;
  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-black mb-4">Impostazioni</h1>
      <div className="flex gap-2 mb-4 border-b border-slate-800">
        <button onClick={() => setTab('users')} className={`px-4 py-2 text-sm font-bold border-b-2 ${tab === 'users' ? 'text-primary border-primary' : 'text-slate-400 border-transparent hover:text-slate-200'}`} data-testid="th-tab-users">
          <UsersIcon size={14} className="inline -mt-0.5 mr-1" /> Utenti
        </button>
        <button onClick={() => setTab('targets')} className={`px-4 py-2 text-sm font-bold border-b-2 ${tab === 'targets' ? 'text-primary border-primary' : 'text-slate-400 border-transparent hover:text-slate-200'}`} data-testid="th-tab-targets">
          <TargetIcon size={14} className="inline -mt-0.5 mr-1" /> Target Forward
        </button>
        <button onClick={() => setTab('gsync')} className={`px-4 py-2 text-sm font-bold border-b-2 ${tab === 'gsync' ? 'text-primary border-primary' : 'text-slate-400 border-transparent hover:text-slate-200'}`} data-testid="th-tab-gsync">
          <Cloud size={14} className="inline -mt-0.5 mr-1" /> Google Sync
        </button>
        <button onClick={() => setTab('webhook')} className={`px-4 py-2 text-sm font-bold border-b-2 ${tab === 'webhook' ? 'text-primary border-primary' : 'text-slate-400 border-transparent hover:text-slate-200'}`} data-testid="th-tab-webhook">
          <Radio size={14} className="inline -mt-0.5 mr-1" /> URL Webhook
        </button>
      </div>
      {tab === 'users' && <UsersTab />}
      {tab === 'targets' && <TargetsTab />}
      {tab === 'gsync' && <GoogleSyncTab />}
      {tab === 'webhook' && <WebhookInfoTab />}
    </div>
  );
}

function UsersTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ email: '', password: '', nome: '' });
  const load = () => api.get('/api/trading-hub/users').then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    try {
      await api.post('/api/trading-hub/users', form);
      setForm({ email: '', password: '', nome: '' });
      load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const toggle = async (u) => { await api.put(`/api/trading-hub/users/${u.id}`, { attivo: !u.attivo }); load(); };
  const del = async (u) => { if (window.confirm(`Eliminare ${u.email}?`)) { await api.delete(`/api/trading-hub/users/${u.id}`); load(); } };
  const resetPwd = async (u) => { const pw = window.prompt(`Nuova password per ${u.email}:`); if (pw) await api.put(`/api/trading-hub/users/${u.id}`, { password: pw }); };
  return (
    <div className={`${cardCls} p-4`}>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
        <input placeholder="Nome" className={inputCls} value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} />
        <input placeholder="Email *" className={inputCls} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
        <input placeholder="Password *" type="password" className={inputCls} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
        <button onClick={add} className={btnPrim} data-testid="th-add-user">Aggiungi utente</button>
      </div>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-800">
          <tr><th className="text-left py-2">Email</th><th className="text-left">Nome</th><th className="text-left">Stato</th><th className="text-right">Azioni</th></tr>
        </thead>
        <tbody>
          {items.map(u => (
            <tr key={u.id} className="border-b border-slate-800/50">
              <td className="py-2 font-mono text-xs">{u.email} {u.is_owner && <span className="ml-1 text-[9px] bg-primary/20 text-primary px-1 rounded">OWNER</span>}</td>
              <td>{u.nome || '—'}</td>
              <td>{u.attivo ? <span className="text-emerald-400 text-xs">attivo</span> : <span className="text-slate-500 text-xs">disattivo</span>}</td>
              <td className="text-right space-x-1">
                {!u.is_owner && <><button onClick={() => resetPwd(u)} className={btnGhost}>Reset PWD</button>
                <button onClick={() => toggle(u)} className={btnGhost}>{u.attivo ? 'Disattiva' : 'Attiva'}</button>
                <button onClick={() => del(u)} className={btnDanger}><Trash2 size={11} /></button></>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TargetsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ nome: '', url: '', metodo: 'POST', auth_type: 'none', auth_header_name: '', auth_value: '', note: '' });
  const load = () => api.get('/api/trading-hub/targets').then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    try {
      await api.post('/api/trading-hub/targets', form);
      setForm({ nome: '', url: '', metodo: 'POST', auth_type: 'none', auth_header_name: '', auth_value: '', note: '' });
      load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const del = async (t) => { if (window.confirm(`Eliminare "${t.nome}"?`)) { await api.delete(`/api/trading-hub/targets/${t.id}`); load(); } };
  return (
    <div className={`${cardCls} p-4`}>
      <p className="text-xs text-slate-400 mb-3">I target sono URL a cui puoi inoltrare manualmente gli eventi ricevuti. Ideali per relay verso bot di trading, altri webhook, servizi di notifica ecc.</p>
      <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-4">
        <input placeholder="Nome" className={inputCls} value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} data-testid="th-target-nome" />
        <input placeholder="URL (https://...)" className={`${inputCls} md:col-span-2`} value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} data-testid="th-target-url" />
        <select className={inputCls} value={form.metodo} onChange={e => setForm({ ...form, metodo: e.target.value })}>
          <option>POST</option><option>PUT</option><option>GET</option>
        </select>
        <select className={inputCls} value={form.auth_type} onChange={e => setForm({ ...form, auth_type: e.target.value })}>
          <option value="none">Auth: nessuna</option>
          <option value="bearer">Bearer token</option>
          <option value="header">Custom header</option>
        </select>
        <button onClick={add} className={btnPrim} data-testid="th-add-target">Crea target</button>
      </div>
      {form.auth_type !== 'none' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
          {form.auth_type === 'header' && <input placeholder="Nome header (es. X-API-Key)" className={inputCls} value={form.auth_header_name} onChange={e => setForm({ ...form, auth_header_name: e.target.value })} />}
          <input placeholder={form.auth_type === 'bearer' ? 'Token (senza \"Bearer\")' : 'Valore header'} type="password" className={inputCls} value={form.auth_value} onChange={e => setForm({ ...form, auth_value: e.target.value })} />
        </div>
      )}
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-800">
          <tr><th className="text-left py-2">Nome</th><th className="text-left">URL</th><th className="text-left">Metodo</th><th className="text-left">Auth</th><th className="text-right">Azioni</th></tr>
        </thead>
        <tbody>
          {items.map(t => (
            <tr key={t.id} className="border-b border-slate-800/50">
              <td className="py-2 font-bold">{t.nome}</td>
              <td className="font-mono text-xs text-slate-400 truncate max-w-[300px]">{t.url}</td>
              <td>{t.metodo}</td>
              <td className="text-xs text-slate-400">{t.auth_type}{t.auth_value_masked ? ` (${t.auth_value_masked})` : ''}</td>
              <td className="text-right"><button onClick={() => del(t)} className={btnDanger}><Trash2 size={11} /></button></td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-slate-500 text-xs">Nessun target configurato.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

const APPS_SCRIPT_CODE = `// EDILCIMA Trading Hub — Apps Script Web App receiver
// 1. Apri script.google.com e crea un nuovo progetto (oppure da un Google Sheet: Estensioni → Apps Script)
// 2. Incolla questo codice e sostituisci SECRET con lo stesso segreto configurato in EDILCIMA
// 3. Distribuisci come App Web (Chi ha accesso: "Chiunque")
// 4. Copia l'URL della web app e incollalo qui in EDILCIMA

const SECRET = 'INCOLLA_QUI_LO_STESSO_SECRET_DI_EDILCIMA';

function doPost(e) {
  try {
    const body = JSON.parse(e.postData.contents);
    if (!body || body.secret !== SECRET) {
      return json({ ok: false, error: 'unauthorized' });
    }

    // === BRANCH 1: upload del CSV completo su Google Drive ===
    if (body.action === 'csv_upload') {
      const filename = body.filename || 'tradingview_snapshots_normalized.csv';
      const folderId = body.folder_id || '';
      const bytes = Utilities.base64Decode(body.content_b64 || '');
      const blob = Utilities.newBlob(bytes, 'text/csv', filename);
      const parent = folderId ? DriveApp.getFolderById(folderId) : DriveApp.getRootFolder();
      // Aggiorna il file esistente con lo stesso nome (se presente) oppure creane uno nuovo
      const it = parent.getFilesByName(filename);
      let file;
      if (it.hasNext()) {
        file = it.next();
        file.setContent(blob.getDataAsString());
      } else {
        file = parent.createFile(blob);
      }
      return json({ ok: true, action: 'csv_upload', file_id: file.getId(),
                    url: file.getUrl(), size_bytes: (body.size_bytes || 0) });
    }

    // === BRANCH 2 (default): append righe a un Google Sheet (attivo o dedicato per simbolo) ===
    const rows = body.rows || [];
    const raws = body.raws || [];
    const sheetName = body.sheet_name || 'TradingView';
    const rawSheetName = body.raw_sheet_name || (sheetName + '_raw');
    const statusSheetName = body.status_sheet_name || 'Status';
    const wantAutoCreate = body.auto_create !== false;
    const symbolKey = body.symbol_key || '';
    const passedSpreadsheetId = body.spreadsheet_id || '';
    const folderId = body.folder_id || '';

    // Risoluzione dello Spreadsheet target:
    // 1. Se il chiamante fornisce spreadsheet_id -> apri quello
    // 2. Altrimenti, se auto_create + symbol_key -> cerca (o crea) un file
    //    "TradingView_<symbolKey>" nella cartella folder_id (o root)
    // 3. Altrimenti usa lo Spreadsheet attivo (comportamento legacy)
    let ss = null;
    let createdInfo = null;
    if (passedSpreadsheetId) {
      try { ss = SpreadsheetApp.openById(passedSpreadsheetId); }
      catch (e) { return json({ ok: false, error: 'spreadsheet_id non apribile: ' + e }); }
    } else if (wantAutoCreate && symbolKey) {
      const desiredName = 'TradingView_' + symbolKey;
      const parent = folderId ? DriveApp.getFolderById(folderId) : DriveApp.getRootFolder();
      const it = parent.getFilesByName(desiredName);
      if (it.hasNext()) {
        ss = SpreadsheetApp.openById(it.next().getId());
      } else {
        // Crea nuovo Spreadsheet, muovilo nella cartella richiesta
        ss = SpreadsheetApp.create(desiredName);
        const newFile = DriveApp.getFileById(ss.getId());
        if (folderId) {
          parent.addFile(newFile);
          try { DriveApp.getRootFolder().removeFile(newFile); } catch (e) { /* ok */ }
        }
        createdInfo = { spreadsheet_id: ss.getId(), spreadsheet_url: ss.getUrl(), name: desiredName };
      }
    } else {
      ss = SpreadsheetApp.getActiveSpreadsheet();
    }
    if (!ss) return json({ ok: false, error: 'Nessuno spreadsheet target. Passa spreadsheet_id, oppure abilita auto_create+symbol_key, oppure apri lo script da un Sheet.' });

    // --- Sheet piatto normalizzato ---
    let sheet = ss.getSheetByName(sheetName) || ss.insertSheet(sheetName);
    if (rows.length > 0) {
      // Header attuale nel Sheet
      let curHeader = sheet.getRange(1, 1, 1, Math.max(1, sheet.getLastColumn())).getValues()[0];
      if (!curHeader[0]) curHeader = [];
      // Header target = header_full (union completa) se fornito, altrimenti union delle keys
      // di tutte le rows in questo batch
      let targetHeader = (body.header_full && body.header_full.length) ? body.header_full.slice() : [];
      if (!targetHeader.length) {
        const seen = {};
        rows.forEach(r => Object.keys(r).forEach(k => { if (!(k in seen)) seen[k] = true; }));
        targetHeader = Object.keys(seen);
      }
      // Aggiungi al target le colonne del Sheet corrente che NON sono nel target
      // (per preservare eventuali colonne aggiunte manualmente dall'utente)
      curHeader.forEach(c => { if (c && targetHeader.indexOf(c) < 0) targetHeader.push(c); });
      // Se l'header e' cambiato (nuove colonne o header vuoto) → riscrivilo
      const headerChanged = curHeader.length !== targetHeader.length ||
                            targetHeader.some((c, i) => c !== curHeader[i]);
      if (headerChanged) {
        // Espandi il numero di colonne se serve
        if (targetHeader.length > sheet.getMaxColumns()) {
          sheet.insertColumnsAfter(sheet.getMaxColumns(), targetHeader.length - sheet.getMaxColumns());
        }
        sheet.getRange(1, 1, 1, targetHeader.length).setValues([targetHeader]);
        sheet.getRange(1, 1, 1, targetHeader.length).setFontWeight('bold');
      }
      const header = targetHeader;
      const matrix = rows.map(r => header.map(k => r[k] === undefined || r[k] === null ? '' : r[k]));
      sheet.getRange(sheet.getLastRow() + 1, 1, matrix.length, header.length).setValues(matrix);
    }

    // --- Sheet raw JSON ---
    if (raws.length > 0) {
      let raw = ss.getSheetByName(rawSheetName) || ss.insertSheet(rawSheetName);
      if (raw.getLastRow() === 0) {
        raw.getRange(1, 1, 1, 3).setValues([['received_at', 'event_id', 'payload_json']]);
        raw.getRange(1, 1, 1, 3).setFontWeight('bold');
      }
      const now = new Date();
      const rowsRaw = raws.map((r, i) => {
        const eid = (r && r.metadata && r.metadata.event_id) || (rows[i] && rows[i].metadata_event_id) || '';
        return [now, eid, JSON.stringify(r)];
      });
      raw.getRange(raw.getLastRow() + 1, 1, rowsRaw.length, 3).setValues(rowsRaw);
    }

    // --- Sheet Status (log operazioni) ---
    let statusSh = ss.getSheetByName(statusSheetName);
    if (!statusSh) {
      statusSh = ss.insertSheet(statusSheetName);
      statusSh.getRange(1, 1, 1, 4).setValues([['ts', 'symbol_key', 'rows_appended', 'note']]);
      statusSh.getRange(1, 1, 1, 4).setFontWeight('bold');
    }
    statusSh.appendRow([new Date(), symbolKey || '', rows.length, createdInfo ? 'sheet_created' : 'append']);

    return json({ ok: true, appended: rows.length,
                  spreadsheet_id: ss.getId(), spreadsheet_url: ss.getUrl(),
                  created: !!createdInfo });
  } catch (err) {
    return json({ ok: false, error: String(err) });
  }
}

function json(obj) {
  return ContentService.createTextOutput(JSON.stringify(obj)).setMimeType(ContentService.MimeType.JSON);
}

function doGet() { return json({ ok: true, service: 'EDILCIMA sync', ts: new Date().toISOString() }); }`;

function GoogleSyncTab() {
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [symbols, setSymbols] = useState([]);
  const [pushSymbol, setPushSymbol] = useState('');

  const load = useCallback(async () => {
    const [c, s, sy] = await Promise.all([
      api.get('/api/trading-hub/sync/config'),
      api.get('/api/trading-hub/sync/stats'),
      api.get('/api/trading-hub/symbols'),
    ]);
    setCfg(c.data); setForm(c.data); setStats(s.data); setSymbols(sy.data || []);
  }, []);
  useEffect(() => { load(); const id = setInterval(() => api.get('/api/trading-hub/sync/stats').then(r => setStats(r.data)).catch(()=>{}), 5000); return () => clearInterval(id); }, [load]);

  // Route editor helpers
  const routes = form.routes || [];
  const setRoute = (idx, patch) => setForm({ ...form, routes: routes.map((r,i) => i===idx ? { ...r, ...patch } : r) });
  const addRoute = () => setForm({ ...form, routes: [...routes, { symbol: '', sheet_name: '', drive_filename: '', drive_folder_id: '' }] });
  const removeRoute = (idx) => setForm({ ...form, routes: routes.filter((_,i) => i!==idx) });

  const urlValidWarning = form.url && !/^https:\/\/script\.google\.com\/macros\/s\/[^/]+\/exec/.test(form.url)
    ? "L'URL non sembra un Web App Apps Script /exec. Deve iniziare con https://script.google.com/macros/s/… e terminare con /exec."
    : null;

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      // Non re-invio secret se e' solo la maschera (form.secret non impostato)
      if (!payload.secret) delete payload.secret;
      await api.put('/api/trading-hub/sync/config', payload);
      await load();
      alert('Configurazione salvata.');
    } catch (e) { alert('Errore: ' + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true); setTestResult(null);
    try {
      const r = await api.post('/api/trading-hub/sync/test', {});
      setTestResult(r.data);
    } catch (e) { setTestResult({ ok: false, message: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  const flush = async () => {
    setBusy(true);
    try {
      const r = await api.post('/api/trading-hub/sync/flush');
      alert('Flush eseguito. Batch: ' + (r.data.batch_size || 0) + ' righe.');
      load();
    } catch (e) { alert('Errore: ' + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const retryDlq = async () => {
    if (!window.confirm('Rimettere in coda tutti gli eventi in Dead Letter Queue?')) return;
    setBusy(true);
    try {
      const r = await api.post('/api/trading-hub/sync/retry-dlq');
      alert(`${r.data.requeued} eventi ripristinati in coda.`);
      load();
    } catch (e) { alert('Errore: ' + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };
  const pushCsv = async () => {
    const label = pushSymbol ? `il CSV filtrato per ${pushSymbol}` : "l'intero file CSV normalizzato";
    if (!window.confirm(`Inviare ${label} a Google Drive?\n(Sovrascrive il file con lo stesso nome nella cartella indicata.)`)) return;
    setBusy(true); setTestResult(null);
    try {
      const qs = pushSymbol ? `?symbol=${encodeURIComponent(pushSymbol)}` : '';
      const r = await api.post(`/api/trading-hub/sync/push-csv${qs}`);
      setTestResult({
        ok: r.data.ok,
        message: r.data.ok
          ? `${r.data.message || 'CSV inviato'} (${r.data.size_bytes} bytes) come "${r.data.filename}". File ID: ${r.data.response?.file_id || '—'} — ${r.data.response?.url || ''}`
          : (r.data.error || 'Errore sconosciuto'),
      });
    } catch (e) { setTestResult({ ok: false, message: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  const copyScript = () => { navigator.clipboard?.writeText(APPS_SCRIPT_CODE); setCopied(true); setTimeout(()=>setCopied(false), 2000); };

  if (!cfg || !stats) return <div className="text-slate-500 text-sm">Caricamento...</div>;

  return (
    <div className="space-y-5" data-testid="th-gsync-tab">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Sincronizzati" value={stats.synced.toLocaleString('it-IT')} />
        <StatCard label="In coda" value={stats.pending} />
        <StatCard label="Falliti (retry)" value={stats.failed} />
        <StatCard label="Dead Letter" value={stats.dlq} />
      </div>

      {stats.breaker_open && (
        <div className="bg-red-950/40 border border-red-900 rounded-md p-3 text-xs text-red-200 flex items-center gap-2">
          <Pause size={14}/> <span><b>Circuit breaker aperto</b> — troppe chiamate fallite di fila. Riprende alle {new Date(stats.breaker_open_until).toLocaleTimeString('it-IT')}. Verifica URL/secret di Apps Script.</span>
        </div>
      )}
      {stats.last_error && !stats.breaker_open && (
        <div className="bg-amber-950/40 border border-amber-900 rounded-md p-3 text-xs text-amber-200">
          <p className="font-bold flex items-center gap-1 mb-1"><AlertTriangle size={12}/> Ultimo errore</p>
          <p className="font-mono break-all">{stats.last_error}</p>
        </div>
      )}
      {stats.last_synced_at && (
        <p className="text-xs text-slate-500">Ultima sincronizzazione: <b className="text-emerald-400">{new Date(stats.last_synced_at).toLocaleString('it-IT')}</b> · event_id <span className="font-mono">{stats.last_synced_event_id}</span></p>
      )}

      {/* Config */}
      <div className={`${cardCls} p-4`}>
        <div className="flex items-center gap-2 mb-3">
          <Cloud size={16} className="text-primary" />
          <h3 className="font-bold text-sm">Configurazione</h3>
        </div>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} data-testid="th-gsync-enabled" />
            <span>Attivo — invia automaticamente gli eventi normalizzati ad Apps Script</span>
          </label>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">URL Apps Script (deploy web app)</label>
            <input className={`${inputCls} font-mono text-xs`} placeholder="https://script.google.com/macros/s/AKfyc.../exec"
              value={form.url || ''} onChange={e => setForm({ ...form, url: e.target.value })} data-testid="th-gsync-url" />
            {urlValidWarning && (
              <p className="text-[10px] text-amber-400 mt-1 flex items-start gap-1" data-testid="th-gsync-url-warning">
                <AlertTriangle size={11} className="flex-shrink-0 mt-0.5"/> {urlValidWarning}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">
                Shared secret {cfg.secret_masked && <span className="text-slate-400 normal-case">(attuale: <code>{cfg.secret_masked}</code>)</span>}
              </label>
              <input type="password" className={inputCls} placeholder={cfg.secret_masked ? "Lascia vuoto per non cambiare" : "Genera uno shared secret"}
                value={form.secret || ''} onChange={e => setForm({ ...form, secret: e.target.value })} data-testid="th-gsync-secret" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Nome foglio (tab Google Sheet)</label>
              <input className={inputCls} placeholder="TradingView" value={form.sheet_name || ''} onChange={e => setForm({ ...form, sheet_name: e.target.value })} data-testid="th-gsync-sheet" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Modalità</label>
              <select className={inputCls} value={form.mode || 'auto'} onChange={e => setForm({ ...form, mode: e.target.value })} data-testid="th-gsync-mode">
                <option value="auto">Automatico (reattivo ai webhook)</option>
                <option value="interval">Solo periodico</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Intervallo max (sec)</label>
              <input type="number" min="3" max="3600" className={inputCls} value={form.interval_seconds || 15} onChange={e => setForm({ ...form, interval_seconds: parseInt(e.target.value || '15', 10) })} data-testid="th-gsync-interval" />
              <p className="text-[10px] text-slate-500 mt-1">Fallback se non arrivano webhook. In modalità Automatica l'invio parte anche subito appena arriva un evento.</p>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Batch max</label>
              <input type="number" min="1" max="200" className={inputCls} value={form.max_batch || 50} onChange={e => setForm({ ...form, max_batch: parseInt(e.target.value || '50', 10) })} data-testid="th-gsync-batch" />
              <p className="text-[10px] text-slate-500 mt-1">Numero massimo di righe per chiamata ad Apps Script.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-slate-800">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Nome file CSV su Drive</label>
              <input className={inputCls} placeholder="tradingview_snapshots_normalized.csv" value={form.drive_filename || ''} onChange={e => setForm({ ...form, drive_filename: e.target.value })} data-testid="th-gsync-drive-filename" />
              <p className="text-[10px] text-slate-500 mt-1">Estensione .csv aggiunta automaticamente se manca.</p>
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">ID cartella Drive (opzionale)</label>
              <input className={`${inputCls} font-mono text-xs`} placeholder="1AbCdEfGhIjK... (vuoto = root)" value={form.drive_folder_id || ''} onChange={e => setForm({ ...form, drive_folder_id: e.target.value })} data-testid="th-gsync-drive-folder" />
              <p className="text-[10px] text-slate-500 mt-1">Copia l&apos;ID dall&apos;URL della cartella: <code>drive.google.com/drive/folders/<b>ID</b></code>.</p>
            </div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button onClick={save} disabled={busy} className={btnPrim} data-testid="th-gsync-save"><Check size={13} /> Salva configurazione</button>
          <button onClick={test} disabled={busy} className={btnGhost} data-testid="th-gsync-test"><Zap size={13} /> Test connessione</button>
          <button onClick={flush} disabled={busy} className={btnGhost} data-testid="th-gsync-flush"><Play size={13} /> Flush ora</button>
          <div className="flex items-center gap-1 ml-2">
            <select className={`${inputCls} !py-1 !w-40 text-xs`} value={pushSymbol} onChange={e => setPushSymbol(e.target.value)} data-testid="th-gsync-push-symbol">
              <option value="">Tutti i simboli</option>
              {symbols.map(s => <option key={s.symbol} value={s.symbol}>{s.symbol} ({s.count})</option>)}
            </select>
            <button onClick={pushCsv} disabled={busy} className={btnGhost} data-testid="th-gsync-push-csv"><Cloud size={13} /> Salva CSV su Drive</button>
          </div>
          {stats.dlq > 0 && (
            <button onClick={retryDlq} disabled={busy} className={btnGhost}><RefreshCw size={13} /> Recupera {stats.dlq} da DLQ</button>
          )}
        </div>
        {testResult && (
          <div className={`mt-3 p-3 rounded-md border text-xs ${testResult.ok ? 'bg-emerald-950/40 border-emerald-800 text-emerald-200' : 'bg-red-950/40 border-red-900 text-red-200'}`} data-testid="th-gsync-test-result">
            <p className="font-bold flex items-center gap-1">{testResult.ok ? <Check size={13}/> : <AlertTriangle size={13}/>} {testResult.ok ? 'Test OK' : 'Test fallito'}</p>
            <p className="font-mono break-all mt-1">{testResult.message}</p>
          </div>
        )}
      </div>

      {/* Routes per-simbolo */}
      <div className={`${cardCls} p-4`}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-sm flex items-center gap-2"><TargetIcon size={16} className="text-primary"/> Routing per simbolo</h3>
          <button onClick={addRoute} className={btnGhost} data-testid="th-gsync-add-route"><Send size={13}/> Aggiungi route</button>
        </div>
        <p className="text-[11px] text-slate-400 mb-3">Ogni route dice a EDILCIMA su quale <b>tab del Sheet</b> e su quale <b>file/cartella Drive</b> mandare i dati per uno specifico simbolo. Usa <code>*</code> come simbolo per una route catch-all (tutti gli altri).</p>
        {routes.length === 0 && (
          <p className="text-xs text-slate-500 italic mb-2">Nessuna route configurata. In assenza, tutti i simboli vanno nel tab "{form.sheet_name || 'TradingView'}" e nel file "{form.drive_filename || 'tradingview_snapshots_normalized.csv'}".</p>
        )}
        {routes.length > 0 && (
          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 px-1">
              <div className="col-span-2">Simbolo</div>
              <div className="col-span-2">Sheet tab</div>
              <div className="col-span-2">File CSV Drive</div>
              <div className="col-span-2">Folder ID</div>
              <div className="col-span-3">Sheet auto-creato</div>
              <div className="col-span-1"></div>
            </div>
            {routes.map((r, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`th-gsync-route-${idx}`}>
                <input className={`${inputCls} col-span-2 font-mono !py-1.5`} placeholder="SPY o *" value={r.symbol || ''} onChange={e => setRoute(idx, { symbol: e.target.value })} />
                <input className={`${inputCls} col-span-2 !py-1.5`} placeholder="TradingView_SPY" value={r.sheet_name || ''} onChange={e => setRoute(idx, { sheet_name: e.target.value })} />
                <input className={`${inputCls} col-span-2 !py-1.5 font-mono text-xs`} placeholder="spy.csv" value={r.drive_filename || ''} onChange={e => setRoute(idx, { drive_filename: e.target.value })} />
                <input className={`${inputCls} col-span-2 !py-1.5 font-mono text-xs`} placeholder="folder ID" value={r.drive_folder_id || ''} onChange={e => setRoute(idx, { drive_folder_id: e.target.value })} />
                <div className="col-span-3 min-w-0">
                  {r.spreadsheet_id ? (
                    <a href={r.spreadsheet_url || `https://docs.google.com/spreadsheets/d/${r.spreadsheet_id}`} target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline text-xs flex items-center gap-1 truncate" data-testid={`th-gsync-route-sheet-${idx}`}>
                      <Check size={11}/> {r.auto_created ? 'auto-creato' : 'collegato'} · Apri Sheet
                    </a>
                  ) : (
                    <input className={`${inputCls} !py-1.5 font-mono text-[10px]`} placeholder="incolla spreadsheet_id (opzionale)" value={r.spreadsheet_id || ''} onChange={e => setRoute(idx, { spreadsheet_id: e.target.value })} />
                  )}
                </div>
                <button onClick={() => removeRoute(idx)} className={`${btnDanger} !py-1.5`} data-testid={`th-gsync-route-del-${idx}`}><Trash2 size={12}/></button>
              </div>
            ))}
            <p className="text-[10px] text-slate-500 mt-2">Ricordati di premere <b>Salva configurazione</b>. La colonna "Sheet auto-creato" si popola quando Apps Script crea per la prima volta il file per il simbolo.</p>
          </div>
        )}
      </div>

      {/* Guida Apps Script */}
      <div className={`${cardCls} p-4`}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-sm flex items-center gap-2"><FileSpreadsheet size={16} className="text-primary"/> Come configurare Apps Script (5 minuti)</h3>
          <a href="https://script.google.com" target="_blank" rel="noreferrer" className={btnGhost}><ExternalLink size={13}/> Apri script.google.com</a>
        </div>
        <ol className="text-sm text-slate-300 space-y-1.5 mb-3 list-decimal list-inside">
          <li>Apri <b>Google Sheets</b> e crea un nuovo foglio vuoto (o apri uno esistente).</li>
          <li>Dal menu <b>Estensioni → Apps Script</b> apri l'editor.</li>
          <li>Elimina il codice presente e incolla il codice qui sotto. Sostituisci <code>INCOLLA_QUI_LO_STESSO_SECRET_DI_EDILCIMA</code> con lo stesso valore che salvi nel campo "Shared secret" qui a sinistra.</li>
          <li>Clicca <b>Distribuisci → Nuova distribuzione → Tipo: App web</b> → "Esegui come: Me" → "Chi ha accesso: Chiunque" → <b>Distribuisci</b>.</li>
          <li>Copia l'URL della web app (finisce in <code>/exec</code>) e incollalo qui in <b>URL Apps Script</b>. Poi salva e premi <b>Test connessione</b>.</li>
        </ol>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Codice Apps Script</p>
          <button onClick={copyScript} className={btnGhost}><Copy size={13}/> {copied ? 'Copiato!' : 'Copia'}</button>
        </div>
        <pre className="text-[10px] font-mono bg-slate-950 border border-slate-800 rounded-md p-3 overflow-auto max-h-80 whitespace-pre-wrap break-all">{APPS_SCRIPT_CODE}</pre>
        <p className="text-[10px] text-slate-500 mt-2">Il codice crea automaticamente due tab: <b>{form.sheet_name || 'TradingView'}</b> (righe piatte) e <b>{(form.sheet_name || 'TradingView') + '_raw'}</b> (JSON grezzo). Inoltre, se premi <b>Salva CSV su Drive ora</b>, lo stesso script salva/aggiorna il file <b>{form.drive_filename || 'tradingview_snapshots_normalized.csv'}</b> nella tua cartella Drive (usa DriveApp — richiede autorizzazione al primo utilizzo).</p>
      </div>
    </div>
  );
}

function WebhookInfoTab() {
  const [copied, setCopied] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;
  // Il token webhook e' un secret server-side: lo mostriamo solo se l'utente lo digita
  const [token, setToken] = useState('');
  const url = token ? `${backend}/api/webhooks/tradingview?token=${encodeURIComponent(token)}` : `${backend}/api/webhooks/tradingview?token=<IL_TUO_TOKEN>`;
  const copy = () => { navigator.clipboard?.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className={`${cardCls} p-6`}>
      <p className="text-sm text-slate-300 mb-4">Usa questo URL come <b>Webhook URL</b> nel tuo alert TradingView. Il token e' un segreto configurato lato server (env <code className="text-primary">TRADING_HUB_WEBHOOK_TOKEN</code>).</p>
      <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1">Il tuo webhook token (per generare l'URL)</label>
      <input type="password" className={`${inputCls} mb-3`} value={token} onChange={e => setToken(e.target.value)} placeholder="incolla qui il TRADING_HUB_WEBHOOK_TOKEN" />
      <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1">URL da incollare in TradingView</label>
      <div className="flex gap-2">
        <input readOnly value={url} className={`${inputCls} font-mono text-xs`} data-testid="th-webhook-url" />
        <button onClick={copy} className={btnPrim}><Copy size={13} /> {copied ? 'Copiato' : 'Copia'}</button>
      </div>
      <div className="mt-6 p-4 bg-slate-950 border border-slate-800 rounded-md">
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-2">Esempio configurazione alert TradingView</p>
        <ul className="text-xs space-y-1.5 text-slate-300 list-disc pl-4">
          <li><b>Webhook URL:</b> l'URL sopra</li>
          <li><b>Message:</b> qualsiasi payload JSON (es. il tuo schema `tv-research.v1`)</li>
          <li>TradingView invia POST con Content-Type dipendente dal template. Il sistema accetta sia JSON che text/plain e lo salva grezzo se non e' JSON.</li>
        </ul>
      </div>
    </div>
  );
}

// ── Analytics: CSV normalizzato + grafico ──────────────────────────
const AVAILABLE_OVERLAYS = [
  { key: 'technical_1m_sma20', label: 'SMA20', color: '#f59e0b' },
  { key: 'technical_1m_sma50', label: 'SMA50', color: '#22d3ee' },
  { key: 'technical_1m_sma200', label: 'SMA200', color: '#a78bfa' },
];
const SECONDARY_CHARTS = [
  { field: 'technical_1m_rsi14', label: 'RSI 1m (14)', color: '#f97316' },
  { field: 'market_context_vix_close', label: 'VIX close', color: '#ef4444' },
  { field: 'volume_spy_bar_volume', label: 'Volume SPY (bar)', color: '#60a5fa' },
];

function Analytics({ me }) {
  const [stats, setStats] = useState(null);
  const [overlays, setOverlays] = useState({ technical_1m_sma20: false, technical_1m_sma50: false, technical_1m_sma200: false });
  const [primary, setPrimary] = useState({ rows: [], fields: [] });
  const [secondary, setSecondary] = useState({});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [symbols, setSymbols] = useState([]);
  const [chartType, setChartType] = useState('line'); // 'line' | 'candle'
  // Simbolo persisted in URL (?symbol=)
  const [symbol, setSymbol] = useState(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('symbol') || '';
  });
  const [overview, setOverview] = useState(null);
  // sync URL su cambio simbolo
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const u = new URL(window.location.href);
    if (symbol) u.searchParams.set('symbol', symbol);
    else u.searchParams.delete('symbol');
    window.history.replaceState({}, '', u.toString());
  }, [symbol]);

  useEffect(() => { api.get('/api/trading-hub/symbols').then(r => setSymbols(r.data || [])).catch(()=>{}); }, []);

  const loadAll = useCallback(async () => {
    setBusy(true);
    try {
      const activeOverlays = Object.entries(overlays).filter(([, v]) => v).map(([k]) => k);
      const overlaysParam = activeOverlays.length ? `&overlays=${encodeURIComponent(activeOverlays.join(','))}` : '';
      const symParam = symbol ? `&symbol=${encodeURIComponent(symbol)}` : '';
      // Chiamata singola aggregata (sostituisce 5-6 richieste parallele) +
      // preview CSV in parallelo (non incluso nel bundle).
      const [bundleRes, previewRes] = await Promise.all([
        api.get(`/api/trading-hub/analytics/bundle?limit=500${symParam}${overlaysParam}`),
        api.get('/api/trading-hub/csv/preview?rows=3&cols=12').catch(() => ({ data: null })),
      ]);
      const b = bundleRes.data || {};
      setStats(b.stats || null);
      setPrimary(b.primary || { rows: [], fields: [] });
      setSecondary({
        [SECONDARY_CHARTS[0].field]: (b.secondary || {})[SECONDARY_CHARTS[0].field] || { rows: [], fields: [] },
        [SECONDARY_CHARTS[1].field]: (b.secondary || {})[SECONDARY_CHARTS[1].field] || { rows: [], fields: [] },
        [SECONDARY_CHARTS[2].field]: (b.secondary || {})[SECONDARY_CHARTS[2].field] || { rows: [], fields: [] },
      });
      setPreview(previewRes.data);
      setOverview(b.overview || null);
    } catch (e) { /* silently */ }
    finally { setBusy(false); }
  }, [overlays, symbol]);

  useEffect(() => { loadAll(); const id = setInterval(loadAll, 30000); return () => clearInterval(id); }, [loadAll]);

  const downloadCsv = async () => {
    try {
      const r = await api.get('/api/trading-hub/csv/download', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'tradingview_snapshots_normalized.csv';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.response?.status === 404 ? 'CSV non ancora generato. Riceverai eventi validi v0.3.2 per crearlo.' : 'Errore download: ' + e.message);
    }
  };

  const rebuild = async () => {
    if (!window.confirm("Ricostruire il CSV a partire da tutti gli eventi salvati nel database?")) return;
    try {
      setBusy(true);
      const r = await api.post('/api/trading-hub/csv/rebuild');
      alert(`Ricostruzione completata:\n${r.data.rows} righe · ${r.data.columns} colonne\nSkipped: ${r.data.skipped} · Invalid: ${r.data.invalid}`);
      loadAll();
    } catch (e) { alert('Errore: ' + (e.response?.data?.detail || e.message)); }
    finally { setBusy(false); }
  };

  // Prepara dati per Recharts: X = bar_close_time (unix ms), converto etichetta HH:mm:ss
  const chartData = useMemo(() => {
    return (primary.rows || [])
      .filter(r => r.metadata_bar_close_time && r.micro_1m_close != null)
      .map(r => {
        const open = r.micro_1m_open;
        const close = r.micro_1m_close;
        const high = r.micro_1m_high;
        const low = r.micro_1m_low;
        return {
          t: r.metadata_bar_close_time,
          label: new Date(r.metadata_bar_close_time).toLocaleTimeString('it-IT'),
          close, open, high, low,
          // Body candela: [min(open,close), max(open,close)] — Recharts bar `range`
          body: (open != null && close != null) ? [Math.min(open, close), Math.max(open, close)] : null,
          // Wick: [low, high]
          wick: (high != null && low != null) ? [low, high] : null,
          bullish: (open != null && close != null) ? close >= open : true,
          sma20: r.technical_1m_sma20,
          sma50: r.technical_1m_sma50,
          sma200: r.technical_1m_sma200,
        };
      });
  }, [primary.rows]);

  const hasOHLC = chartData.some(d => d.body != null);
  const effectiveChartType = chartType === 'candle' && !hasOHLC ? 'line' : chartType;

  const secondaryData = (field) => {
    const rows = secondary[field]?.rows || [];
    return rows.filter(r => r.metadata_bar_close_time && r[field] != null)
      .map(r => ({ label: new Date(r.metadata_bar_close_time).toLocaleTimeString('it-IT'), v: r[field] }));
  };

  // Statistiche del dataset filtrato in memoria (coerenti con simbolo selezionato)
  const filteredStats = useMemo(() => {
    const rows = primary.rows || [];
    const cols = primary.fields || [];
    const last = rows[rows.length - 1] || {};
    return {
      rows: rows.length,
      columns: cols.length,
      last_event_id: last.metadata_event_id || null,
      last_bar_close_time: last.metadata_bar_close_time || null,
      last_symbol: last.metadata_symbol || null,
    };
  }, [primary]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5">
      {/* Stats cards - coerenti con simbolo selezionato */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label={symbol ? `Righe (${symbol})` : 'Righe normalizzate (tutte)'} value={filteredStats.rows.toLocaleString('it-IT')} />
        <StatCard label="Colonne" value={filteredStats.columns || (stats?.columns || 0)} />
        <StatCard label="Ultimo event_id" value={filteredStats.last_event_id ? String(filteredStats.last_event_id).slice(0, 24) + '…' : '—'} mono />
        <StatCard label="Ultimo bar_close" value={filteredStats.last_bar_close_time ? new Date(filteredStats.last_bar_close_time).toLocaleString('it-IT') : '—'} />
        <StatCard label="Ultimo aggiornamento" value={stats?.last_update ? new Date(stats.last_update).toLocaleString('it-IT') : '—'} />
      </div>

      {/* Actions & path */}
      <div className={`${cardCls} p-4 flex flex-wrap items-center gap-2`}>
        <FileSpreadsheet size={16} className="text-primary" />
        <span className="text-xs font-mono text-slate-400 flex-1 min-w-0 truncate" title={stats?.csv_path}>{stats?.csv_path || 'CSV non ancora generato'}</span>
        <label className="flex items-center gap-1 text-xs text-slate-300">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Simbolo:</span>
          <select value={symbol} onChange={e => setSymbol(e.target.value)} className={`${inputCls} !w-40 !py-1 text-xs`} data-testid="th-analytics-symbol">
            <option value="">Tutti (panoramica)</option>
            {symbols.filter(s => s.symbol_key !== 'UNCLASSIFIED').map(s => <option key={s.symbol_key || s.symbol} value={s.symbol || s.symbol_key}>{s.symbol_display || s.symbol} ({s.count})</option>)}
          </select>
        </label>
        <button onClick={downloadCsv} className={btnPrim} data-testid="th-csv-download"><Download size={13} /> Scarica CSV normalizzato</button>
        {me?.is_owner && (
          <button onClick={rebuild} disabled={busy} className={btnGhost} data-testid="th-csv-rebuild"><RefreshCw size={13} /> Ricostruisci da DB</button>
        )}
        <button onClick={loadAll} disabled={busy} className={btnGhost}><RefreshCw size={13} /> Aggiorna</button>
      </div>

      {stats?.last_error && (
        <div className="bg-red-950/40 border border-red-900 rounded-md p-3 text-xs text-red-200" data-testid="th-last-error">
          <p className="font-bold mb-1 flex items-center gap-1"><AlertTriangle size={12}/> Ultimo errore di normalizzazione</p>
          <pre className="whitespace-pre-wrap">{stats.last_error}</pre>
        </div>
      )}

      {/* Panoramica per simbolo (mostrata solo quando "Tutti" e' selezionato) */}
      {!symbol && Array.isArray(overview) && overview.length > 0 && (
        <div className={`${cardCls} p-4`} data-testid="th-analytics-overview">
          <p className="text-sm font-bold mb-3 flex items-center gap-2"><Radio size={14} className="text-primary"/> Panoramica per simbolo <span className="text-slate-500 font-normal text-xs">— Seleziona un simbolo per il grafico prezzo dedicato</span></p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {overview.map(o => (
              <button key={o.symbol_key} onClick={() => setSymbol(o.symbol)} className="text-left p-3 border border-slate-800 rounded-md hover:border-primary/60 hover:bg-slate-900/60 transition-colors" data-testid={`th-overview-card-${o.symbol_key}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-mono text-sm font-bold">{o.symbol_display}</span>
                  <span className="text-[10px] text-slate-500">{o.count} eventi</span>
                </div>
                {o.last_close != null && (
                  <p className="mt-1 text-lg font-black tabular-nums">{o.last_close.toLocaleString('it-IT', { maximumFractionDigits: 2 })}</p>
                )}
                <p className="text-[10px] text-slate-500 mt-1">{o.last_at ? new Date(o.last_at).toLocaleString('it-IT') : '—'}</p>
                <div className="flex gap-2 mt-1.5 text-[10px]">
                  {o.synced > 0 && <span className="text-emerald-400">✓ {o.synced}</span>}
                  {o.pending > 0 && <span className="text-amber-400">⏳ {o.pending}</span>}
                  {o.errors > 0 && <span className="text-red-400">✗ {o.errors}</span>}
                </div>
              </button>
            ))}
          </div>
          <p className="text-[10px] text-slate-500 mt-3 italic">Il grafico prezzo aggregato non viene mostrato quando "Tutti" e' selezionato: i prezzi di simboli diversi non sono confrontabili.</p>
        </div>
      )}

      {/* Primary chart */}
      <div className={`${cardCls} p-4`}>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <p className="text-sm font-bold">{effectiveChartType === 'candle' ? 'Candele OHLC (1m)' : 'micro_1m_close nel tempo'}</p>
            {/* Toggle Linea / Candele */}
            <div className="flex items-center gap-0.5 border border-slate-800 rounded-md p-0.5" data-testid="th-chart-type-toggle">
              <button onClick={() => setChartType('line')} className={`text-[10px] px-2 py-0.5 rounded ${effectiveChartType==='line' ? 'bg-primary text-slate-900 font-bold' : 'text-slate-400 hover:text-slate-200'}`} data-testid="th-chart-line">Linea</button>
              <button onClick={() => setChartType('candle')} disabled={!hasOHLC} title={!hasOHLC ? 'OHLC non disponibili per questo simbolo' : ''} className={`text-[10px] px-2 py-0.5 rounded ${effectiveChartType==='candle' ? 'bg-primary text-slate-900 font-bold' : 'text-slate-400 hover:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed'}`} data-testid="th-chart-candle">Candele</button>
            </div>
            {chartType === 'candle' && !hasOHLC && (
              <span className="text-[10px] text-amber-400" title="Nessun campo OHLC nelle righe filtrate">⚠ OHLC non disponibili, uso linea</span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {AVAILABLE_OVERLAYS.map(o => (
              <label key={o.key} className="flex items-center gap-1 text-xs cursor-pointer">
                <input type="checkbox" checked={!!overlays[o.key]} onChange={e => setOverlays({ ...overlays, [o.key]: e.target.checked })} data-testid={`th-overlay-${o.key}`} />
                <span style={{ color: o.color }}>{o.label}</span>
              </label>
            ))}
          </div>
        </div>
        {chartData.length === 0 || !symbol ? (
          <div className="h-64 flex items-center justify-center text-slate-500 text-sm text-center px-4" data-testid="th-primary-chart-empty">
            {!symbol ? 'Seleziona un simbolo dal dropdown per vedere il grafico prezzo. I prezzi di simboli diversi non sono confrontabili in un unico grafico.' : 'Nessun dato normalizzato per questo simbolo. Attendi webhook v0.3.2 validi...'}
          </div>
        ) : effectiveChartType === 'candle' ? (
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={chartData} barCategoryGap={2}>
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
              <XAxis dataKey="label" stroke="#94a3b8" fontSize={10} />
              <YAxis stroke="#94a3b8" fontSize={10} domain={['auto', 'auto']} />
              <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6 }} labelStyle={{ color: '#e2e8f0' }}
                formatter={(value, name, props) => {
                  if (name === 'wick' || name === 'body') return null;
                  return [value, name];
                }}
                content={({ active, payload, label }) => {
                  if (!active || !payload || !payload.length) return null;
                  const d = payload[0]?.payload || {};
                  return (
                    <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, padding: 8, fontSize: 11 }}>
                      <div style={{ color: '#e2e8f0', marginBottom: 4 }}>{label}</div>
                      <div style={{ color: '#94a3b8' }}>O: <b style={{ color: '#e2e8f0' }}>{d.open}</b></div>
                      <div style={{ color: '#94a3b8' }}>H: <b style={{ color: '#4ade80' }}>{d.high}</b></div>
                      <div style={{ color: '#94a3b8' }}>L: <b style={{ color: '#f87171' }}>{d.low}</b></div>
                      <div style={{ color: '#94a3b8' }}>C: <b style={{ color: '#e2e8f0' }}>{d.close}</b></div>
                    </div>
                  );
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {/* Wick (linea sottile high-low) */}
              <Bar dataKey="wick" name="high-low" barSize={2} isAnimationActive={false}>
                {chartData.map((d, i) => (<Cell key={`w-${i}`} fill={d.bullish ? '#22c55e' : '#ef4444'} />))}
              </Bar>
              {/* Body (open-close) */}
              <Bar dataKey="body" name="open-close" barSize={8} isAnimationActive={false}>
                {chartData.map((d, i) => (<Cell key={`b-${i}`} fill={d.bullish ? '#22c55e' : '#ef4444'} />))}
              </Bar>
              {AVAILABLE_OVERLAYS.map(o => overlays[o.key] && (
                <Line key={o.key} type="monotone" dataKey={o.key.replace('technical_1m_', '')} name={o.label} stroke={o.color} strokeWidth={1.5} dot={false} isAnimationActive={false} />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <RCLineChart data={chartData}>
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
              <XAxis dataKey="label" stroke="#94a3b8" fontSize={10} />
              <YAxis stroke="#94a3b8" fontSize={10} domain={['auto', 'auto']} />
              <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6 }} labelStyle={{ color: '#e2e8f0' }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="close" name="close" stroke="#FFED00" strokeWidth={2} dot={false} isAnimationActive={false} />
              {AVAILABLE_OVERLAYS.map(o => overlays[o.key] && (
                <Line key={o.key} type="monotone" dataKey={o.key.replace('technical_1m_', '')} name={o.label} stroke={o.color} strokeWidth={1.5} dot={false} isAnimationActive={false} />
              ))}
            </RCLineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Secondary charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {SECONDARY_CHARTS.map(sc => {
          const primaryDataArr = secondaryData(sc.field);
          const isVix = sc.field === 'market_context_vix_close';
          let data = primaryDataArr;
          let fallbackUsed = null;
          if (isVix && data.length === 0) {
            const alt1 = secondaryData('market_context_vix_current_close');
            const alt2 = secondaryData('vix_close');
            if (alt1.length > 0) { data = alt1; fallbackUsed = 'market_context_vix_current_close'; }
            else if (alt2.length > 0) { data = alt2; fallbackUsed = 'vix_close'; }
          }
          return (
            <div key={sc.field} className={`${cardCls} p-3`}>
              <p className="text-xs font-bold mb-2" style={{ color: sc.color }}>{sc.label}
                {fallbackUsed && <span className="ml-2 text-[9px] font-normal text-slate-500">(fallback: {fallbackUsed})</span>}
              </p>
              {data.length === 0 ? (
                <div className="h-32 flex flex-col items-center justify-center text-slate-500 text-[10px] text-center px-2">
                  <span>Nessun dato per <code className="text-slate-400">{sc.field}</code></span>
                  {isVix && <span className="text-[9px] mt-1">Tentati anche: market_context_vix_current_close, vix_close</span>}
                  {primary.rows?.length > 0 && <span className="text-[9px] mt-1 opacity-70">({primary.rows.length} righe totali {symbol ? `per ${symbol}` : ''})</span>}
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={140}>
                  <RCLineChart data={data}>
                    <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                    <XAxis dataKey="label" stroke="#94a3b8" fontSize={9} hide />
                    <YAxis stroke="#94a3b8" fontSize={9} domain={['auto', 'auto']} />
                    <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, fontSize: 11 }} />
                    <Line type="monotone" dataKey="v" stroke={sc.color} strokeWidth={1.5} dot={false} isAnimationActive={false} />
                  </RCLineChart>
                </ResponsiveContainer>
              )}
            </div>
          );
        })}
      </div>

      {/* Preview CSV */}
      {preview && preview.total_columns > 0 && (
        <div className={`${cardCls} p-4`}>
          <p className="text-sm font-bold mb-2">Anteprima CSV — prime {preview.columns_preview.length} colonne di {preview.total_columns}, ultime {preview.sample_rows.length} righe</p>
          <div className="overflow-x-auto">
            <table className="text-[10px] font-mono w-full">
              <thead>
                <tr className="border-b border-slate-800">
                  {preview.columns_preview.map(c => <th key={c} className="text-left py-1 px-2 text-slate-500 whitespace-nowrap">{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {preview.sample_rows.map((r, i) => (
                  <tr key={i} className="border-b border-slate-800/50">
                    {preview.columns_preview.map(c => <td key={c} className="py-1 px-2 whitespace-nowrap">{r[c] ?? '—'}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, mono }) {
  return (
    <div className={`${cardCls} p-3`}>
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</p>
      <p className={`mt-1 text-lg font-black ${mono ? 'font-mono' : ''}`}>{value}</p>
    </div>
  );
}


// ── Root component ──────────────────────────────────────────────────
export default function TradingHubApp() {
  const { me, loading, reload, setMe } = useMe();
  const navigate = useNavigate();

  const onLogout = () => { clearToken(); setMe(null); navigate('/th-portal'); };

  if (loading) return <div className={`${shellBg} flex items-center justify-center`}><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!me) return <Login onLogged={() => reload()} />;

  return (
    <div className={shellBg}>
      <Routes>
        <Route path="/" element={<><TopBar me={me} onLogout={onLogout} active="home" /><Dashboard me={me} /></>} />
        <Route path="/analytics" element={<><TopBar me={me} onLogout={onLogout} active="analytics" /><Analytics me={me} /></>} />
        <Route path="/settings" element={<><TopBar me={me} onLogout={onLogout} active="settings" /><Settings me={me} /></>} />
        <Route path="*" element={<Navigate to="/th-portal" replace />} />
      </Routes>
    </div>
  );
}
